import { Box, Button, TextField, Typography } from '@mui/material';
import { FormEvent, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRequest } from '../../hooks/useRequest';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { refreshCurrentUser } from '../../store/slices/auth-slice';
import { setContentfulPat } from '../../utils/axios/api';

export function PatPage() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const hasContentfulPat = useAppSelector(
    (state) => state.auth.user?.hasContentfulPat ?? false,
  );
  const { fetch, isLoading, exception } = useRequest();
  const [token, setToken] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      const trimmedToken = token.trim();

      if (!trimmedToken) {
        setValidationError(t('settings.pat.errors.required'));
        return;
      }

      setValidationError(null);

      const result = await fetch(async () => {
        await setContentfulPat(trimmedToken);
        await dispatch(refreshCurrentUser()).unwrap();
      });
      if (result === null) {
        return;
      }
      setToken('');
    },
    [dispatch, fetch, t, token],
  );

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      autoComplete="off"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.25,
      }}
    >
      <Typography variant="subtitle1">
        {t('settings.pat.description')}
      </Typography>
      <TextField
        type="password"
        label={t('settings.pat.label')}
        value={token}
        onChange={(event) => setToken(event.target.value)}
        placeholder={hasContentfulPat ? '********' : undefined}
        slotProps={{
          inputLabel: { shrink: hasContentfulPat || undefined },
          htmlInput: {
            autoComplete: 'new-password',
            autoCorrect: 'off',
            autoCapitalize: 'off',
            spellCheck: false,
          },
        }}
        error={Boolean(validationError ?? exception)}
        helperText={
          validationError ??
          (exception ? t(`exceptions.${exception}`) : undefined)
        }
        disabled={isLoading}
        autoComplete="new-password"
        fullWidth
      />
      <Button
        type="submit"
        variant="contained"
        disabled={isLoading}
        sx={{ alignSelf: 'flex-start' }}
      >
        {t('settings.pat.save')}
      </Button>
    </Box>
  );
}
